import { cn } from "@lib/cn"
import type { ReactNode } from "react"

/**
 * An empty state teaches the surface (PRD-59); it does not report absence.
 * Every one of these in the product used to be a title plus a sentence, so a
 * professor's first week was a tour of polite dead ends. The shape now is:
 * what this surface is for, the one action that fills it, and — when the
 * surface is genuinely a destination rather than a list — what will appear
 * here once it does.
 */
export function EmptyState({
  title,
  message,
  action,
  secondaryAction,
  hint,
  icon,
  className,
}: {
  title: string
  message?: string
  /** The single move that resolves the emptiness. */
  action?: ReactNode
  secondaryAction?: ReactNode
  /** What lands here once the action is taken. Keep to one short line each. */
  hint?: ReactNode
  icon?: ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        "rounded-card border-border bg-surface border border-dashed px-6 py-10 text-center",
        className,
      )}
    >
      {icon && (
        <div
          aria-hidden
          className="bg-accent-soft text-accent-ink mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full"
        >
          {icon}
        </div>
      )}
      <h2 className="text-ink text-base font-semibold">{title}</h2>
      {message && <p className="text-ink-muted mx-auto mt-1 max-w-prose text-sm">{message}</p>}
      {(action || secondaryAction) && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {action}
          {secondaryAction}
        </div>
      )}
      {hint && <div className="text-meta mx-auto mt-4 max-w-prose text-left">{hint}</div>}
    </div>
  )
}
